<template>
  <div>
    <v-container>
      <v-data-table
        :headers="headers"
        :items="templates"
        :loading="loading"
        item-key="template_id"
        :item-class="rowClass"
        hide-default-footer
        class="templates-table"
        no-data-text="No export templates yet">
        <template v-slot:[`item.columns`]="{ item }">
          {{ (item.config && item.config.fields ? item.config.fields.length : 0) }}
        </template>
        <template v-slot:[`item.filters`]="{ item }">
          {{ filterSummary(item) }}
        </template>
        <template v-slot:[`item.updated_on`]="{ item }">
          {{ formatDate(item.updated_on) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-3" v-on="on" @click="editTemplate(item)">mdi-pencil</v-icon>
            </template>
            <span>Edit</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-3" v-on="on" @click="duplicateTemplate(item)">mdi-content-copy</v-icon>
            </template>
            <span>Duplicate</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small color="error" v-on="on" @click="promptDelete(item)">mdi-delete</v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <v-divider class="mt-4 mb-4"></v-divider>

      <div class="template-form">
        <div class="subtitle-2 mb-2">{{ editingId ? 'Edit template' : 'New template' }}</div>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
              v-model="form.name"
              label="Template name"
              dense outlined hide-details>
            </v-text-field>
          </v-col>
        </v-row>

        <div class="d-flex align-center mt-3 mb-1">
          <span class="caption grey--text text--darken-1">Columns</span>
          <v-checkbox
            :input-value="allColumnsSelected"
            :indeterminate="someColumnsSelected && !allColumnsSelected"
            label="Select all"
            dense hide-details
            class="mt-0 pt-0 ml-4"
            @change="toggleAllColumns">
          </v-checkbox>
        </div>
        <v-row no-gutters>
          <v-col
            v-for="(detail, index) in detailSelectionList"
            :key="index"
            cols="3">
            <v-checkbox
              v-model="form.fieldIds"
              :value="detail.id"
              :label="detail.label"
              dense hide-details
              class="mt-0">
            </v-checkbox>
          </v-col>
        </v-row>

        <div class="caption mt-3 mb-1 grey--text text--darken-1">Filters (optional)</div>
        <v-row dense>
          <v-col cols="6">
            <v-select
              v-model="form.filters.item_sources"
              :items="userSources"
              item-text="display_name"
              item-value="source_id"
              label="Sources"
              multiple small-chips
              dense outlined hide-details clearable>
            </v-select>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.filters.description"
              label="Description contains"
              dense outlined hide-details clearable>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="4">
            <v-select
              v-model="form.filters.departments"
              :items="departmentOptions"
              label="Departments"
              multiple small-chips
              dense outlined hide-details clearable>
            </v-select>
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="form.filters.categories"
              :items="categoryOptions"
              label="Categories"
              multiple small-chips
              dense outlined hide-details clearable>
            </v-select>
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="form.filters.subcategories"
              :items="subcategoryOptions"
              label="Subcategories"
              multiple small-chips
              dense outlined hide-details clearable>
            </v-select>
          </v-col>
        </v-row>
        <v-row dense v-if="showStatus">
          <v-col cols="4">
            <v-select
              v-model="form.filters.status"
              :items="statusOptions"
              label="Status"
              dense outlined hide-details clearable>
            </v-select>
          </v-col>
        </v-row>
        <v-row v-if="otherFilterKeys.length" dense>
          <v-col cols="12" class="pt-1">
            <span class="caption grey--text text--darken-1">
              Also filters by: {{ otherFilterLabel }} (kept as saved)
            </span>
          </v-col>
        </v-row>
        <v-row v-if="error" dense>
          <v-col cols="12" class="pt-0">
            <span class="error--text caption">{{ error }}</span>
          </v-col>
        </v-row>
      </div>
    </v-container>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn v-if="editingId" text class="mr-4" @click="resetForm">Cancel edit</v-btn>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!canSave"
        @click="saveTemplate">
        {{ editingId ? 'Update' : 'Create' }}
      </v-btn>
    </v-card-actions>

    <v-dialog v-model="showDeleteConfirm" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">Delete export template?</v-card-title>
        <v-card-text>
          Delete <strong>{{ pendingDelete ? pendingDelete.name : '' }}</strong>? Any
          scheduled deliveries using it will be removed as well.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" text @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import Items from '@/api/endpoints'
import { displayAlert } from '@/mixins/alert'
import { userAccess } from '@/mixins/user-access'
import { buildExportColumns } from '@/components/items/data/exportColumns'

const SUPPORTED_FILTER_KEYS = ['item_sources', 'departments', 'categories', 'subcategories', 'description', 'status']

const FILTER_LABELS = {
  item_sources: 'Sources',
  departments: 'Departments',
  categories: 'Categories',
  subcategories: 'Subcategories',
  description: 'Description',
  status: 'Status',
  vendor_ids: 'Vendors',
  item_id: 'Item Id',
  upc: 'UPC',
  gtin: 'GTIN',
  private_label: 'Private Label',
  shipper_flag: 'Shipper',
  linked_group: 'Linked Group'
}

function filterLabel (key) {
  return FILTER_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export default {
  name: 'ExportTemplatesPanel',
  mixins: [displayAlert, userAccess],
  data () {
    return {
      templates: [],
      loading: false,
      saving: false,
      error: '',
      editingId: null,
      showDeleteConfirm: false,
      pendingDelete: null,
      form: this.emptyForm(),
      headers: [
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Columns', value: 'columns', sortable: false, align: 'center' },
        { text: 'Filters', value: 'filters', sortable: false },
        { text: 'Updated', value: 'updated_on', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ],
      statusOptions: ['VALID', 'DISCONTINUED ITEM', 'TEMP OUT']
    }
  },
  async mounted () {
    await this.fetchTemplates()
  },
  computed: {
    detailSelectionList () {
      return buildExportColumns(this.$auth.tenant, this.isRetailer)
    },
    showStatus () {
      return this.$auth.tenant === 'jbg' && this.isAdmin
    },
    departmentCategoryMapping () {
      return this.$store.getters.departmentCategoryMapping || []
    },
    categorySubcategoryMapping () {
      return this.$store.getters.categorySubcategoryMapping || []
    },
    departmentOptions () {
      let departments = [...new Set(this.departmentCategoryMapping.map(i => i.column))]
      if (this.isRetailer) {
        departments = departments.filter(d => !['LIQUOR WAREHOUSE'].includes(d))
      }
      return departments.sort((a, b) => a.localeCompare(b))
    },
    categoryOptions () {
      const selectedDepts = this.form.filters.departments || []
      let categories = selectedDepts.length > 0
        ? [...new Set(this.departmentCategoryMapping.filter(i => selectedDepts.includes(i.column)).map(i => i.downstream_column))]
        : [...new Set(this.departmentCategoryMapping.map(i => i.downstream_column))]
      if (this.isRetailer) {
        categories = categories.filter(c => !['BEER', 'WINE', 'SPIRITS'].includes(c))
      }
      return categories.sort((a, b) => a.localeCompare(b))
    },
    subcategoryOptions () {
      const selectedCats = this.form.filters.categories || []
      let subcategories
      if (selectedCats.length > 0) {
        subcategories = [...new Set(this.categorySubcategoryMapping.filter(i => selectedCats.includes(i.column)).map(i => i.downstream_column))]
      } else if ((this.form.filters.departments || []).length > 0) {
        subcategories = [...new Set(this.categorySubcategoryMapping.filter(i => this.categoryOptions.includes(i.column)).map(i => i.downstream_column))]
      } else {
        subcategories = [...new Set(this.categorySubcategoryMapping.map(i => i.downstream_column))]
      }
      return subcategories.sort((a, b) => a.localeCompare(b))
    },
    otherFilterKeys () {
      return Object.keys(this.form.filters).filter(k => {
        if (SUPPORTED_FILTER_KEYS.includes(k)) {
          return false
        }
        const v = this.form.filters[k]
        return Array.isArray(v) ? v.length > 0 : Boolean(v)
      })
    },
    otherFilterLabel () {
      return this.otherFilterKeys.map(filterLabel).join(', ')
    },
    allColumnsSelected () {
      return this.detailSelectionList.length > 0 &&
        this.detailSelectionList.every(d => this.form.fieldIds.includes(d.id))
    },
    someColumnsSelected () {
      return this.form.fieldIds.length > 0
    },
    canSave () {
      return Boolean(this.form.name && this.form.name.trim()) && this.form.fieldIds.length > 0
    }
  },
  methods: {
    emptyFilters () {
      return { item_sources: [], departments: [], categories: [], subcategories: [], description: '', status: null }
    },
    emptyForm () {
      return { name: '', fieldIds: [], filters: this.emptyFilters() }
    },
    formatDate (value) {
      return value ? String(value).slice(0, 10) : ''
    },
    filterSummary (item) {
      const filters = (item.config && item.config.filters) || {}
      const keys = Object.keys(filters).filter(k => {
        const v = filters[k]
        return Array.isArray(v) ? v.length > 0 : Boolean(v)
      })
      return keys.length ? keys.map(filterLabel).join(', ') : 'None'
    },
    toggleAllColumns (val) {
      this.form.fieldIds = val ? this.detailSelectionList.map(d => d.id) : []
    },
    rowClass (item) {
      return item.template_id === this.editingId ? 'editing-row' : ''
    },
    async fetchTemplates () {
      this.loading = true
      try {
        const res = await Items.getExportTemplates()
        this.templates = (res && res.items) ? res.items : []
      } catch (err) {
        this.error = 'Unable to load export templates.'
      } finally {
        this.loading = false
      }
    },
    formFromConfig (name, config) {
      const cfg = config || {}
      return {
        name,
        fieldIds: [...(cfg.fields || [])],
        // Keep every saved filter key (incl. ones this editor doesn't manage) so
        // editing never drops filters created from a search.
        filters: { ...this.emptyFilters(), ...(cfg.filters || {}) }
      }
    },
    editTemplate (item) {
      this.error = ''
      this.editingId = item.template_id
      this.form = this.formFromConfig(item.name, item.config)
    },
    duplicateTemplate (item) {
      this.error = ''
      this.editingId = null
      this.form = this.formFromConfig(`${item.name} (copy)`, item.config)
    },
    resetForm () {
      this.editingId = null
      this.form = this.emptyForm()
      this.error = ''
    },
    buildFilters () {
      const out = { ...this.form.filters }
      ;['item_sources', 'departments', 'categories', 'subcategories'].forEach(k => {
        if (!out[k] || out[k].length === 0) {
          delete out[k]
        }
      })
      if (out.description && String(out.description).trim()) {
        out.description = String(out.description).trim()
      } else {
        delete out.description
      }
      if (!out.status) {
        delete out.status
      }
      return out
    },
    buildPayload () {
      return {
        name: this.form.name.trim(),
        config: { fields: this.form.fieldIds, filters: this.buildFilters() }
      }
    },
    async saveTemplate () {
      this.error = ''
      this.saving = true
      const editing = !!this.editingId
      try {
        if (editing) {
          await Items.updateExportTemplate(this.editingId, this.buildPayload())
        } else {
          await Items.createExportTemplate(this.buildPayload())
        }
        this.resetForm()
        await this.fetchTemplates()
        this.emitAlert(true, 'success', editing ? 'Template updated' : 'Template created', undefined, 3000)
      } catch (err) {
        const status = err && err.response ? err.response.status : null
        this.error = status === 409
          ? 'A template with that name already exists.'
          : 'Unable to save the template.'
      } finally {
        this.saving = false
      }
    },
    promptDelete (item) {
      this.pendingDelete = item
      this.showDeleteConfirm = true
    },
    confirmDelete () {
      const item = this.pendingDelete
      this.showDeleteConfirm = false
      this.pendingDelete = null
      if (item) {
        this.deleteTemplate(item)
      }
    },
    async deleteTemplate (item) {
      this.error = ''
      try {
        await Items.deleteExportTemplate(item.template_id)
        if (this.editingId === item.template_id) {
          this.resetForm()
        }
        await this.fetchTemplates()
        this.emitAlert(true, 'success', 'Template deleted', undefined, 3000)
      } catch (err) {
        this.error = 'Unable to delete the template.'
      }
    }
  }
}
</script>
<style scoped>
.templates-table >>> td,
.templates-table >>> th {
  font-size: 0.95rem;
}
.templates-table >>> tr.editing-row td {
  background-color: #e3f2fd;
}
.template-form {
  max-width: 760px;
}
</style>



// WEBPACK FOOTER //
// src/components/ExportTemplatesPanel.vue